import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import { checkboxClasses } from '@liquidweb-dev/undercurrent/Checkbox';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import LockIcon from '@liquidweb-dev/undercurrent/icons/lucide/Lock';
import ScaleIcon from '@liquidweb-dev/undercurrent/icons/lucide/Scale';
import { inputBaseClasses } from '@liquidweb-dev/undercurrent/InputBase';
import InputAdornment from '@liquidweb-dev/undercurrent/InputAdornment';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import { Theme } from '@liquidweb-dev/undercurrent/styles';
import { switchClasses } from '@liquidweb-dev/undercurrent/Switch';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { pseudoClasses } from '@liquidweb-dev/undercurrent/classes/pseudo';
import { formatCurrency } from '@liquidweb-dev/banana-stand/format';

import HookedCurrencyField from 'components/Form/HookedCurrencyField';
import HookedSwitch from 'components/Form/HookedSwitch';
import { useDialogMakePaymentContext } from '../dialogMakePaymentContext';
import getExpectedBalance from './getExpectedBalance';

const getFooterBackgroundColor = (
	{
		payFullBalance,
		hasExpectedCredit,
	}: { payFullBalance: boolean; hasExpectedCredit: boolean },
	themeVars: Theme['vars'],
) => {
	if (payFullBalance) {
		return themeVars.palette.uc.success.bg.main;
	}
	if (hasExpectedCredit) {
		return themeVars.palette.uc.highlight.bg.main;
	}
	return themeVars.palette.uc.bg.weak;
};

const RemainingBalance = ({ sx, ...rest }: BoxProps) => {
	const { formValues, accountBalanceDisplay } = useDialogMakePaymentContext();
	const { amount } = formValues;
	const { expectedCredit, expectedRemainingBalance, hasExpectedCredit } =
		getExpectedBalance(amount, Number(accountBalanceDisplay));

	return (
		<Box
			sx={combineSx(
				{
					display: 'flex',
					alignItems: 'center',
					typography: 'body-sm',
				},
				sx,
			)}
			{...rest}
		>
			<ScaleIcon
				sx={({ size, vars }) => ({
					width: size.xS,
					height: size.xS,
					color: vars.palette.uc.icon.weak,
				})}
			/>
			<Collapse in={!hasExpectedCredit} orientation="horizontal">
				<Typography
					sx={{
						color: ({ vars }) => vars.palette.uc.text.weak,
						whiteSpace: 'nowrap',
						marginLeft: 100,
					}}
				>
					Remaining balance:
				</Typography>
			</Collapse>
			<Typography sx={{ fontWeight: 500, marginLeft: 100 }}>
				{formatCurrency(expectedRemainingBalance)}
			</Typography>

			<Collapse in={hasExpectedCredit} orientation="horizontal">
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 100,
						marginLeft: 200,
						whiteSpace: 'nowrap',
					}}
				>
					<Typography
						variant="body-sm"
						sx={{ color: ({ vars }) => vars.palette.uc.highlight.text.medium }}
					>
						Acct. credit
					</Typography>
					<Typography
						variant="body-sm"
						sx={{
							fontWeight: 500,
							color: ({ vars }) => vars.palette.uc.highlight.text.strong,
						}}
					>
						{formatCurrency(expectedCredit, { plusSign: true })}
					</Typography>
				</Box>
			</Collapse>
		</Box>
	);
};

const AmountField = () => {
	const {
		accountBalanceDisplay,
		formErrors,
		formValues,
		setFormValues,
		setFormErrors,
	} = useDialogMakePaymentContext();
	const { amount, payFullBalance } = formValues;
	const { hasExpectedCredit } = getExpectedBalance(
		amount,
		Number(accountBalanceDisplay),
	);

	const showPayFullBalanceSwitch = Number(accountBalanceDisplay) > 0;

	return (
		<Stack>
			<HookedCurrencyField
				name="amount"
				// UI here is complicated - error helper text throws things off
				// so just rely on danger outline to communicate error state
				suppressErrorHelperText
				sx={{
					[`& .${inputBaseClasses.root}.${inputBaseClasses.error}${pseudoClasses.important}`]:
						{
							backgroundColor: ({ vars }) => vars.palette.uc.bg.main,
						},
				}}
				slotProps={{
					input: {
						endAdornment: payFullBalance && (
							<InputAdornment position="end">
								<LockIcon />
							</InputAdornment>
						),
						readOnly: payFullBalance,
					},
				}}
			/>
			<Box
				sx={({ borderRadius, vars }) => ({
					backgroundColor: () =>
						getFooterBackgroundColor(
							{ payFullBalance, hasExpectedCredit },
							vars,
						),
					borderRadius: borderRadius.rounded,
					marginTop: -150,
					paddingTop: 300,
					paddingX: 200,
					paddingBottom: 150,
					transition: 'background-color 0.3s ease-in-out',
					display: 'flex',
					alignItems: { sm: 'center' },
					justifyContent: 'space-between',
					gap: 100,
					flexDirection: { xs: 'column', sm: 'row' },
				})}
			>
				{showPayFullBalanceSwitch && (
					<HookedSwitch
						name="payFullBalance"
						label="Pay full balance"
						onChange={(event) => {
							if (event.target.checked && accountBalanceDisplay) {
								setFormValues((prev) => ({
									...prev,
									amount: accountBalanceDisplay,
								}));
								// reset amount input any errors
								setFormErrors((prev) => ({ ...prev, amount: '' }));
							}
						}}
						sx={({ vars }) => ({
							[`& .${switchClasses.switchBase}.${checkboxClasses.checked} + .${switchClasses.track}`]:
								{
									backgroundColor: vars.palette.uc.success.bg.strong,
								},
						})}
					/>
				)}

				<RemainingBalance
					sx={{
						opacity: !payFullBalance ? 1 : 0,
						transition: 'opacity 0.3s ease-in-out',
					}}
				/>
			</Box>

			<Collapse in={Boolean(formErrors.amount)}>
				<Typography
					variant="label-lg"
					sx={({ vars }) => ({
						color: vars.palette.uc.danger.text.main,
						marginTop: 100,
					})}
				>
					{formErrors.amount}
				</Typography>
			</Collapse>
		</Stack>
	);
};

export default AmountField;
